import type React from 'react';
import { connect } from 'react-redux';
import { CurrentPage, FirstPage, NextPage, Page, Pages, PaginationContainer, PrevPage } from '@finn-no/troika-react-pagination';

import type { ApiQueryParams, PaleState } from '../../interfaces';
import { fetchResults } from '../../state/resultActions';
import { actions as filterActions } from '../../state/filterActions';

export interface Pagination {
    totalPages: number;
    currentPage: number;
    setCurrentPage: (page?: number) => void;
    triggerFetch: (queryParam?: ApiQueryParams) => void;
}

export const PaginationLinks: React.FC<Pagination> = ({
    totalPages,
    currentPage,
    setCurrentPage,
    triggerFetch,
}): React.ReactElement<Pagination> | null => {
    const setFocusOnFirstResultListItem = (): void => {
        const resultList = document.getElementById('resultList');
        const firstElement = resultList?.getElementsByTagName('button')[0];
        if (firstElement) {
            (firstElement as HTMLElement).focus();
        }
    };

    const renderNewPage = async (page: number): Promise<void> => {
        setCurrentPage(page === 1 ? undefined : page);
        await triggerFetch();
        await setFocusOnFirstResultListItem();
        window.scroll(0, 0);
    };

    const createClick =
        (page: number) =>
        (event: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
            event.preventDefault();
            return renderNewPage(page);
        };

    const renderPagination = (): React.ReactElement<Pagination> | null => (
        <PaginationContainer currentPage={currentPage} lastPage={totalPages} data-testid="pagination">
            <FirstPage href="?page=1" onClick={createClick(1)} data-testid="firstPage" aria-label="Første side" />
            <PrevPage
                href={`?page=${currentPage - 1}`}
                onClick={createClick(currentPage - 1)}
                data-testid={'prevPage'}
                aria-label="Forrige side"
            />
            <Pages data-testid="pageNumbers">
                {(page: number): React.ReactElement => (
                    <Page
                        href={`?page=${page}`}
                        onClick={createClick(page)}
                        data-testid={currentPage === page ? 'selected' : `page${page}`}
                        aria-label={`Side ${page}`}
                    />
                )}
            </Pages>
            <CurrentPage data-testid="paginationText" />
            <NextPage
                href={`?page=${currentPage + 1}`}
                onClick={createClick(currentPage + 1)}
                data-testid={'nextPage'}
                aria-label="Neste side"
            />
        </PaginationContainer>
    );

    return totalPages > 1 ? renderPagination() : null;
};

const mapStateToProps = (state: PaleState): Pick<Pagination, 'totalPages' | 'currentPage'> => ({
    totalPages: state.results.totalPages,
    currentPage: state.results.currentPage,
});

const mapDispatchToProps = {
    setCurrentPage: filterActions.changePage,
    triggerFetch: fetchResults,
};

export default connect(mapStateToProps, mapDispatchToProps)(PaginationLinks);
